import { info } from '@core/js/utils/logger';
import {
    utagLink,
    utagView,
    getProductSellingPrice,
    getProductOriginalPrice,
    getGALinkTrackData
} from '@core/js/utils/analyticsUtils';
import { searchAndReplace } from '../utils/generalUtils';
import { CLIENT_SERVER_ERROR_TRACKING } from '@core/js/enums/trackingEnums';
import {
    LINK_TRACK_ANALYTICS_ACTION,
    INVIEW_TRACK_ANALYTICS_ACTION
} from '@core/js/actions/analyticsActions';
import { getProductCategory } from '@product/js/utils/cartUtils';
import { getScene7ImagePath, getSlug } from '@core/js/filters/mediaPathGetters';

export function constructTealiumMiddleware(arrayOfTagObjects = []) {
    const tags = Object.assign(
            {}, ...arrayOfTagObjects
        );

    return function populateTags({ getState }) {
        return next => action => {
            let cmEvent = null;
            const cmFunctions = [];

            if (action.type === CLIENT_SERVER_ERROR_TRACKING) {
                // Handle client/server side error tracking
                cmFunctions.push(tags.client_server_error_tracking);
            } else if (action.meta && action.meta.event) {
                // Handle cm-elements
                cmEvent = action.meta.event;
                let el = cmEvent.currentTarget;

                if (action.type === INVIEW_TRACK_ANALYTICS_ACTION) {
                    el = action.meta.element;
                }

                if (_.isObject(el) && 'dataset' in el) {
                    const { cmElement, gaLinkTrack, gaInviewTrack } = el.dataset;

                    if (
                        typeof cmElement !== 'undefined' &&
                        cmElement in tags &&
                        action.type !== LINK_TRACK_ANALYTICS_ACTION
                    ) {
                        cmFunctions.push(tags[cmElement]);
                    }

                    if (typeof gaLinkTrack !== 'undefined' && 'ga_link_track' in tags
                        && action.type === LINK_TRACK_ANALYTICS_ACTION) {
                        cmFunctions.push(tags.ga_link_track);
                    }

                    if (typeof gaInviewTrack !== 'undefined' && 'ga_inview_track' in tags
                        && action.type === INVIEW_TRACK_ANALYTICS_ACTION) {
                        cmFunctions.push(tags.ga_inview_track);
                    }
                }
            }

            if (cmFunctions.length > 0) {

                cmFunctions.forEach(cmFunction => {
                    if ('requestIdleCallback' in window) {
                        requestIdleCallback(cmFunction(getState(), action, cmEvent));
                    } else {
                        cmFunction(getState(), action, cmEvent)();
                    }
                });

            }

            return next(action);
        };
    };
}

export function coreTags() {
    /* eslint-disable camelcase */
    return {
        default: (state, action) => { },
        client_server_error_tracking: (state, action) => {
            const cmData = action.payload.errorTag;

            return () => {
                utagLink({
                    event_type: 'errortrack',
                    error_name: cmData
                });
                info('utag.link fired: errortrack');
            };
        },
        ga_link_track: (state, action, event) => {
            const attributeDataset = _.get(action, 'meta.event.currentTarget.dataset');
            let utagLinkData = getGALinkTrackData(attributeDataset);

            // Search occurrences of "PAGE_TYPE" and replace with core.pageType
            utagLinkData = searchAndReplace(utagLinkData, _.get(state, 'core.constants.pageTypeKey'),
                _.get(state, 'core.pageType'));

            // Search occurrences of "TAB_TYPE" and replace with mto.selectedTabName
            utagLinkData = searchAndReplace(utagLinkData, _.get(state, 'core.constants.tabTypeKey'),
                _.get(state, 'mto.selectedTabName'));

            return () => {
                utagLink(utagLinkData);
                info('utag.link fired: ga_link_track:', utagLinkData);
            };
        },
        ga_inview_track: (state, action, event) => {
            const attributeDataset = _.get(action, 'meta.element.dataset');
            let utagLinkData = getGALinkTrackData(attributeDataset, 'gaInview');

            // Search occurrences of "PAGE_TYPE" and replace with core.pageType
            utagLinkData = searchAndReplace(utagLinkData, _.get(state, 'core.constants.pageTypeKey'),
                _.get(state, 'core.pageType'));

            // Search occurrences of "TAB_TYPE" and replace with mto.selectedTabName
            utagLinkData = searchAndReplace(utagLinkData, _.get(state, 'core.constants.tabTypeKey'),
                _.get(state, 'mto.selectedTabName'));

            return () => {
                utagLink(utagLinkData);
                info('utag.link fired: ga_inview_track:', utagLinkData);
            };
        },
        account_login_modal: (state, action) => {

            return () =>{
                utagView({
                    page_type: state.core.localizations.containers.analyticsContainer.account_login_modal_page_type,
                    page_id: state.core.localizations.containers.analyticsContainer.account_login_modal_page_id,
                    category_id: state.core.localizations.containers.analyticsContainer.account_login_modal_category_id,
                    page_name: state.core.localizations.containers.analyticsContainer.account_login_modal_page_name
                });
                info('utag.view fired: account_login_modal');
            };
        },
        account_create_modal: (state, action) => {

            return () =>{
                utagView({
                    page_type: state.core.localizations.containers.analyticsContainer.account_create_modal_page_type,
                    page_id: state.core.localizations.containers.analyticsContainer.account_create_modal_page_id,
                    category_id: state.core.localizations.containers.analyticsContainer.account_create_modal_category_id,
                    page_name: state.core.localizations.containers.analyticsContainer.account_create_modal_page_name
                });
                info('utag.view fired: account_create_modal');
            };
        },
        account_forgot_password: (state, action) => {

            return () =>{
                utagView({
                    page_type: state.core.localizations.containers.analyticsContainer.account_forgot_password_page_type,
                    page_id: state.core.localizations.containers.analyticsContainer.account_forgot_password_page_id,
                    category_id: state.core.localizations.containers.analyticsContainer
                    .account_forgot_password_category_id,
                    page_name: state.core.localizations.containers.analyticsContainer.account_forgot_password_page_name
                });
                info('utag.view fired: account_forgot_password');
            };
        },
        add_to_cart: (state, action, event) => {
            const thirdParty = _.get(state, 'core.constants.thirdParty');
            const productPdpUrl = [window.location.href];
            let eventAction = 'add to cart';
            let targetProduct;
            let productSku;
            let productCategory;
            let productName;
            let productSellingPrice;
            let productOriginalPrice;
            let wishlistCartItem;
            let mtoItem;
            let productMarkdownState;
            let productQuickShip;

            switch (event.currentTarget.dataset.productType) {
                case 'regular':
                    targetProduct = _.isEmpty(state.quickshop) ? state.product : state.quickshop.product;
                    productSku = [_.get(targetProduct, 'selectedSku.sku.skuId', '')];
                    productCategory = [getProductCategory(targetProduct)];
                    productName = [_.get(targetProduct, 'product.displayName', '')];
                    productSellingPrice = getProductSellingPrice(targetProduct);
                    productOriginalPrice = getProductOriginalPrice(targetProduct);
                    productMarkdownState = [_.get(targetProduct, 'skuInfo.markdownState', '')];
                    productQuickShip = _.get(targetProduct, 'selectedSku.sku.quickShipEligible', false) ? ['1'] : ['0'];
                    break;
                case 'family':
                    eventAction = 'family | add to cart';
                    targetProduct = _.find(state.product.familyProducts, (p) =>
                        p.id === event.currentTarget.dataset.productId);
                    productSku = [_.get(targetProduct, 'selectedSku.sku.skuId', '')];
                    productCategory = [getProductCategory(targetProduct)];
                    productName = [_.get(targetProduct, 'product.displayName', '')];
                    productSellingPrice = getProductSellingPrice(targetProduct);
                    productOriginalPrice = getProductOriginalPrice(targetProduct);
                    productMarkdownState = [_.get(targetProduct, 'skuInfo.markdownState', '')];
                    productQuickShip = _.get(targetProduct, 'selectedSku.sku.quickShipEligible', false) ? ['1'] : ['0'];
                    break;
                case 'wishlist':
                    wishlistCartItem = state.wishlist.cart.cartItems[event.currentTarget.dataset.wishlistProductIndex];
                    targetProduct = wishlistCartItem.catalogInfo;
                    productSku = [_.get(wishlistCartItem, 'skuId', '')];
                    productCategory = [_.get(event, 'currentTarget.dataset.categoryId', '')];
                    productName = [_.get(targetProduct, 'product.displayName', '')];
                    productSellingPrice = [`${_.get(wishlistCartItem, 'priceInfo.unitNetPrice', '')}`];
                    productOriginalPrice = [`${_.get(wishlistCartItem, 'priceInfo.unitListPrice', '')}`];
                    productMarkdownState = [_.get(targetProduct, 'skuInfo.markdownState', '')];
                    productQuickShip = _.get(wishlistCartItem, 'catalogInfo.quickShipEligible', false) ? ['1'] : ['0'];
                    break;
                case 'mto':
                    mtoItem = state.mto;
                    targetProduct = state.product;
                    // Name retrieved differently because MTO product model in state diverges from others
                    productName = [_.get(targetProduct, 'displayName', '')];
                    productSku = [_.get(mtoItem, 'skuInfo.includedSkus[0].skuId', '')];
                    productCategory = [getProductCategory(targetProduct)];
                    productSellingPrice = [`${_.get(mtoItem, 'skuInfo.listPriceLow', '')}`];
                    productOriginalPrice = getProductOriginalPrice(mtoItem);
                    productMarkdownState = [_.get(mtoItem, 'skuInfo.markdownState', '')];
                    productQuickShip = ['0'];
                    break;
                default:
                    // do nothing, use default values
            }

            const styleNumber = _.get(targetProduct, 'styleNumber');
            const colorId = _.get(targetProduct, 'productImageViewer.colorCode');
            const viewCode = _.get(targetProduct, 'productMeta.viewCode', 'b');
            const imageSlug = _.get(targetProduct, 'images.0', '') || getSlug(styleNumber, colorId, viewCode);

            return () => {
                const productId = [_.get(targetProduct, 'productId', '')];
                const productQuantity = [_.get(targetProduct, 'productBopis.quantity', '1')];
                const cartProductSku = [''];
                const productStyleNumber = [_.get(targetProduct, 'styleNumber', '')];
                const productAvailablePetite = _.get(targetProduct, 'product.isPetite') ? ['1'] : ['0'];
                const productAvailablePickup = _.get(targetProduct, 'productBopis.disableShipToStore') ? ['0'] : ['1'];
                const productBackInStock = _.get(targetProduct, 'productMeta.backInStock') ? ['1'] : ['0'];
                const productBrand = [_.get(targetProduct, 'product.brand', '')];
                const productCollection = _.get(targetProduct, 'productMeta.isFamilyProduct') ? ['1'] : ['0'];
                const productImageUrl = [`https:${getScene7ImagePath(thirdParty)}${imageSlug}`];
                const productOnPage = [_.get(targetProduct, 'productMeta.familyProductIds', '')];
                const productOnlineExclusive = _.get(targetProduct, 'product.webExclusive') ? ['1'] : ['0'];
                const productQuickshop = _.isEmpty(state.quickshop) ? ['0'] : ['1'];
                const productReviewRating = [_.get(targetProduct, 'reviews.averageRating', '')];
                const productReviewsCount = [_.get(targetProduct, 'reviews.count', '')];
                const productSaleItem = _.get(targetProduct, 'product.skuInfo.hasMarkdown') ? ['1'] : ['0'];
                const productSlug = [_.get(targetProduct, 'productSlug', '')];
                const productSoldOut = _.get(targetProduct, 'isSoldOut') ? ['1'] : ['0'];
                const productView = _.isEmpty(state.quickshop) ? ['PDP'] : ['Quickshop'];
                const productHasVideo = _.get(targetProduct, 'product.hasVideo') ? ['1'] : ['0'];
                const productIsVintage = _.get(targetProduct, 'product.isVintage') ? ['1'] : ['0'];
                const productIsPreorder = _.get(targetProduct, 'selectedSku.availabilityInfo.isPreOrder')
                ? ['1'] : ['0'];
                const productBrowseCallOut1 = [_.get(targetProduct, 'product.browseCallOutPrimary', '')];
                const productBrowseCallOut2 = [_.get(targetProduct, 'product.browseCallOutSecondary', '')];
                const productPdpCallOut1 = [_.get(targetProduct, 'product.pdpCallOutTextPrimary', '')];
                const productPdpCallOut2 = [_.get(targetProduct, 'product.pdpCallOutTextSecondary', '')];
                const isCustomProduct = _.get(targetProduct, 'configurationEnabled') ? ['1'] : ['0'];
                const isMarketPlace = _.get(targetProduct, 'product.isMarketPlace') ? ['1'] : ['0'];
                const marketplaceSellerName = [_.get(targetProduct, 'product.sellerInfo.shopName', '')];
                const marketplaceOfferId = [_.get(targetProduct, 'selectedSku.sku.offerId', '')];
                const afterpayStatus = [_.get(targetProduct, 'selectedSku.sku.afterpay.status', '')];
                const afterpayPayment = [_.get(targetProduct, 'selectedSku.sku.afterpay.payment', '').toString()];
                const editorialContent = [_.get(targetProduct, 'product.creativeContent', '')];
                const customerId = _.get(state, 'core.profile.id', '');
                const customerSession = _.get(state, 'core.profile.tracerValue', '');
                const categoryId = _.get(targetProduct, 'browseCategory', '');
                const pageType = _.get(state, 'core.pageType');

                const utagLinkData = {
                    event_label: productName[0],
                    event_category: pageType,
                    event_action: eventAction,
                    cart_product_sku: cartProductSku,
                    product_id: productId,
                    product_quantity: productQuantity,
                    product_sku: productSku,
                    product_style_number: productStyleNumber,
                    product_category: productCategory,
                    product_selling_price: productSellingPrice,
                    product_original_price: productOriginalPrice,
                    product_available_petite: productAvailablePetite,
                    product_available_pickup: productAvailablePickup,
                    product_backinstock: productBackInStock,
                    product_brand: productBrand,
                    product_collection: productCollection,
                    product_image_url: productImageUrl,
                    product_pdp_url: productPdpUrl,
                    product_name: productName,
                    product_on_page: productOnPage,
                    product_online_exclusive: productOnlineExclusive,
                    product_quickship: productQuickShip,
                    product_quickshop: productQuickshop,
                    product_review_rating: productReviewRating,
                    product_reviews_count: productReviewsCount,
                    product_sale_item: productSaleItem,
                    product_slug: productSlug,
                    product_sold_out: productSoldOut,
                    product_view: productView,
                    product_has_video: productHasVideo,
                    product_markdown_state: productMarkdownState,
                    product_is_vintage: productIsVintage,
                    product_is_preorder: productIsPreorder,
                    product_browse_call_out_1: productBrowseCallOut1,
                    product_browse_call_out_2: productBrowseCallOut2,
                    product_pdp_call_out_1: productPdpCallOut1,
                    product_pdp_call_out_2: productPdpCallOut2,
                    product_is_custom: isCustomProduct,
                    product_is_marketplace: isMarketPlace,
                    product_seller_name: marketplaceSellerName,
                    product_offer_id: marketplaceOfferId,
                    afterpay_status: afterpayStatus,
                    afterpay_installment_amount: afterpayPayment,
                    product_editorial_content: editorialContent,
                    customer_id: customerId,
                    customer_session: customerSession,
                    category_id: categoryId
                };
                utagLink(utagLinkData);
                info('utag.link fired: add_to_cart', utagLinkData);
            };
        }
    };
    /* eslint-enable camelcase */
}
